import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LogoImage } from "@/components/ui/logo-image";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { PageTitleProvider, usePageTitle } from "@/contexts/page-title-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { HeaderActions } from "@/components/ui/header-actions";
import { Heading } from "@/components/ui/heading";
import { Icon } from "@/components/ui/icons";
import { HStack } from "@/components/ui/h-stack";
import { IconButton } from "@/components/ui/icon-button";
import { Surface } from "@/components/ui/surface";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import { lazyComponent } from "@/lib/lazy-component";
const QrScannerModal = lazyComponent(() =>
  import("@/components/qr-scanner-modal").then((m) => ({
    default: m.QrScannerModal,
  })),
);
import { extractQrCode } from "@/lib/qr-utils";
import { APP_URL } from "@/lib/env";
import { getCurrentUser, signOut, type AuthUser } from "@/lib/api/auth";
import { useAuthStorageSync } from "@/hooks/useAuthStorageSync";
import {
  UserEntityProvider,
  useUserEntity,
} from "@/contexts/user-entity-context";
import { WorkspaceModeProvider } from "@/contexts/workspace-mode-context";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  getBrokerSidebarSections,
  setBrokerSidebarSections,
} from "@/lib/sidebar-preferences";
import { getRoleUiTokens } from "@/lib/role-ui";
import { useTheme } from "@/contexts/theme-context";

type NavItemConfig = {
  href: string;
  label: string;
  icon: React.FC;
  badge?: number;
};

const BROKER_SECTION_DEFAULTS: Record<string, boolean> = {
  "shipper-ops": true,
  "carrier-ops": true,
  "support-account": true,
};

const ADMIN_SECTION_DEFAULTS: Record<string, boolean> = {
  catalogs: true,
};

/** Non-admin routes that admins may access (shared links, QR codes, etc.) */
const ADMIN_ALLOWED_ROUTES = [
  "/account",
  "/loads",
  "/notifications",
  "/negotiations",
  "/tickets",
  "/trips",
  "/qr",
] as const;

type AdminNavLabels = {
  dashboard: string;
  adminSection: string;
  applications: string;
  loadApprovals: string;
  trips: string;
  entities: string;
  globalSettings: string;
  supportTickets: string;
  system: string;
  catalogsSection: string;
  loadTypes: string;
  loadDocumentKinds: string;
  tripEventPolicies: string;
  dangerousCategories: string;
  truckCatalog: string;
  truckTypes: string;
  trailerTypes: string;
  trailerLoadTypes: string;
  currencies: string;
  countries: string;
  agreementTerms: string;
  profile: string;
};

type ShipperNavLabels = {
  dashboard: string;
  myLoads: string;
  negotiations: string;
  trips: string;
  addresses: string;
  contacts: string;
  tickets: string;
  account: string;
};

type CarrierNavLabels = {
  dashboard: string;
  loadBoard: string;
  negotiations: string;
  trips: string;
  fleet: string;
  drivers: string;
  tickets: string;
  account: string;
};

type BrokerNavLabels = {
  overviewDashboard: string;
  shipperOperations: string;
  postManageLoads: string;
  receivedBids: string;
  loadTracking: string;
  clientList: string;
  carrierOperations: string;
  findLoads: string;
  myOffersSent: string;
  fleetDispatch: string;
  providerList: string;
  supportAndAccount: string;
  tickets: string;
  account: string;
};

type DriverNavLabels = {
  dashboard: string;
  myTrips: string;
  account: string;
};

type RoleBadgeLabels = {
  admin: string;
  carrier: string;
  broker: string;
  driver: string;
  shipper: string;
  owner: string;
  manager: string;
  operator: string;
  forwarder: string;
  accountant: string;
};

const getAdminNavConfig = (labels: AdminNavLabels) => {
  return {
    overview: {
      href: "/admin/dashboard",
      label: labels.dashboard,
      icon: Icon.Dashboard,
      badge: 0,
    },
    sections: [
      {
        id: "admin",
        title: labels.adminSection,
        items: [
          {
            href: "/admin/applications",
            label: labels.applications,
            icon: Icon.FileText,
            badge: 0,
          },
          {
            href: "/admin/loads",
            label: labels.loadApprovals,
            icon: Icon.Loads,
            badge: 0,
          },
          {
            href: "/admin/trips",
            label: labels.trips,
            icon: Icon.Trips,
            badge: 0,
          },
          {
            href: "/admin/entities",
            label: labels.entities,
            icon: Icon.Building,
            badge: 0,
          },
          {
            href: "/admin/global-settings",
            label: labels.globalSettings,
            icon: Icon.Document,
            badge: 0,
          },
          {
            href: "/admin/support-tickets",
            label: labels.supportTickets,
            icon: Icon.Ticket,
            badge: 0,
          },
          {
            href: "/admin/system",
            label: labels.system,
            icon: Icon.Computer,
            badge: 0,
          },
        ],
      },
      {
        id: "catalogs",
        title: labels.catalogsSection,
        items: [
          {
            href: "/admin/catalogs/load-types",
            label: labels.loadTypes,
            icon: Icon.Archive,
            badge: 0,
          },
          {
            href: "/admin/catalogs/load-document-kinds",
            label: labels.loadDocumentKinds,
            icon: Icon.Document,
            badge: 0,
          },
          {
            href: "/admin/catalogs/trip-event-policies",
            label: labels.tripEventPolicies,
            icon: Icon.Clock,
            badge: 0,
          },
          {
            href: "/admin/catalogs/dangerous-categories",
            label: labels.dangerousCategories,
            icon: Icon.Alert,
            badge: 0,
          },
          {
            href: "/admin/catalogs/truck-catalog",
            label: labels.truckCatalog,
            icon: Icon.Truck,
            badge: 0,
          },
          {
            href: "/admin/catalogs/truck-types",
            label: labels.truckTypes,
            icon: Icon.Truck,
            badge: 0,
          },
          {
            href: "/admin/catalogs/trailer-types",
            label: labels.trailerTypes,
            icon: Icon.Truck,
            badge: 0,
          },
          {
            href: "/admin/catalogs/trailer-load-types",
            label: labels.trailerLoadTypes,
            icon: Icon.Truck,
            badge: 0,
          },
          {
            href: "/admin/catalogs/currencies",
            label: labels.currencies,
            icon: Icon.Document,
            badge: 0,
          },
          {
            href: "/admin/catalogs/countries",
            label: labels.countries,
            icon: Icon.Map,
            badge: 0,
          },
          {
            href: "/admin/catalogs/agreement-terms",
            label: labels.agreementTerms,
            icon: Icon.Document,
            badge: 0,
          },
        ],
      },
    ],

    profile: {
      href: "/admin/profile",
      label: labels.profile,
      icon: Icon.Profile,
      badge: 0,
    },
  };
};

// Navigation items for Shipper role
const getShipperNavItems = (labels: ShipperNavLabels) => {
  return [
    {
      href: "/dashboard",
      label: labels.dashboard,
      icon: Icon.Dashboard,
      badge: 0,
    },
    { href: "/loads", label: labels.myLoads, icon: Icon.Loads, badge: 0 },
    {
      href: "/negotiations",
      label: labels.negotiations,
      icon: Icon.Negotiations,
      badge: 0,
    },
    {
      href: "/trips",
      label: `${labels.trips} (Tracking)`,
      icon: Icon.Trips,
      badge: 0,
    },
    {
      href: "/addresses",
      label: labels.addresses,
      icon: Icon.Location,
      badge: 0,
    },
    {
      href: "/contacts",
      label: labels.contacts,
      icon: Icon.Contacts,
      badge: 0,
    },
    {
      href: "/tickets",
      label: labels.tickets,
      icon: Icon.Ticket,
      badge: 0,
    },
    { href: "/account", label: labels.account, icon: Icon.Profile, badge: 0 },
  ];
};

// Navigation items for Carrier role (TransportCompany)
const getCarrierNavItems = (labels: CarrierNavLabels) => {
  return [
    {
      href: "/dashboard",
      label: labels.dashboard,
      icon: Icon.Dashboard,
      badge: 0,
    },
    {
      href: "/load-board",
      label: labels.loadBoard,
      icon: Icon.Loads,
      badge: 0,
    },
    {
      href: "/negotiations",
      label: labels.negotiations,
      icon: Icon.Negotiations,
      badge: 0,
    },
    {
      href: "/trips",
      label: `${labels.trips} (Tracking)`,
      icon: Icon.Trips,
      badge: 0,
    },
    {
      href: "/fleet",
      label: labels.fleet,
      icon: Icon.Truck,
      badge: 0,
    },
    {
      href: "/drivers",
      label: labels.drivers,
      icon: Icon.User,
      badge: 0,
    },
    {
      href: "/tickets",
      label: labels.tickets,
      icon: Icon.Ticket,
      badge: 0,
    },
    { href: "/account", label: labels.account, icon: Icon.Profile, badge: 0 },
  ];
};

// Navigation items for Driver role
const getDriverNavItems = (labels: DriverNavLabels) => {
  return [
    {
      href: "/dashboard",
      label: labels.dashboard,
      icon: Icon.Dashboard,
      badge: 0,
    },
    {
      href: "/trips",
      label: labels.myTrips,
      icon: Icon.Trips,
      badge: 0,
    },
    { href: "/account", label: labels.account, icon: Icon.Profile, badge: 0 },
  ];
};

// Navigation items for Broker role (hybrid of Shipper + Carrier)
const getBrokerNavConfig = (labels: BrokerNavLabels) => {
  const overview: NavItemConfig = {
    href: "/dashboard",
    label: labels.overviewDashboard,
    icon: Icon.Dashboard,
    badge: 0,
  };

  const sections: Array<{ id: string; title: string; items: NavItemConfig[] }> =
    [
      {
        id: "shipper-ops",
        title: labels.shipperOperations,
        items: [
          {
            href: "/loads",
            label: labels.postManageLoads,
            icon: Icon.Loads,
            badge: 0,
          },
          {
            href: "/negotiations/received",
            label: labels.receivedBids,
            icon: Icon.Negotiations,
            badge: 0,
          },
          {
            href: "/trips/shipper",
            label: labels.loadTracking,
            icon: Icon.Trips,
            badge: 0,
          },
          {
            href: "/shippers",
            label: labels.clientList,
            icon: Icon.Building,
            badge: 0,
          },
        ],
      },
      {
        id: "carrier-ops",
        title: labels.carrierOperations,
        items: [
          {
            href: "/load-board",
            label: labels.findLoads,
            icon: Icon.Search,
            badge: 0,
          },
          {
            href: "/negotiations/sent",
            label: labels.myOffersSent,
            icon: Icon.Negotiations,
            badge: 0,
          },
          {
            href: "/trips/carrier",
            label: labels.fleetDispatch,
            icon: Icon.Trips,
            badge: 0,
          },
          {
            href: "/carriers",
            label: labels.providerList,
            icon: Icon.Truck,
            badge: 0,
          },
        ],
      },
      {
        id: "support-account",
        title: labels.supportAndAccount,
        items: [
          {
            href: "/tickets",
            label: labels.tickets,
            icon: Icon.Ticket,
            badge: 0,
          },
          {
            href: "/account",
            label: labels.account,
            icon: Icon.Profile,
            badge: 0,
          },
        ],
      },
    ];

  return { overview, sections };
};

interface NavItemProps {
  href: string;
  label: string;
  icon: React.FC;
  active: boolean;
  badge?: number;
  onClick?: () => void;
}

const NavItem = ({
  href,
  label,
  icon: Icon,
  active,
  badge,
  onClick,
}: NavItemProps) => {
  const navId = href.replace("/", "") || "dashboard";
  return (
    <Link
      to={href}
      data-testid={`nav-item-${navId}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
        active
          ? "bg-[color:var(--surface-container-high)] text-[color:var(--primary)] font-medium"
          : "text-[color:var(--on-surface-variant)] hover:bg-[color:var(--surface-container-low)] hover:text-[color:var(--on-surface)]"
      }`}
    >
      <Icon />
      <Text className="flex-1">{label}</Text>
      {badge !== undefined && badge > 0 && (
        <Badge
          color="danger"
          size="xs"
          className="w-5 h-5 rounded-full flex items-center justify-center"
        >
          {badge}
        </Badge>
      )}
    </Link>
  );
};

function NavSection({
  id,
  title,
  items,
  open,
  onToggle,
  isActiveHref,
  onItemClick,
}: {
  id: string;
  title: string;
  items: NavItemConfig[];
  open: boolean;
  onToggle: () => void;
  isActiveHref: (href: string) => boolean;
  onItemClick?: () => void;
}) {
  const hasActiveChild = items.some((item) => isActiveHref(item.href));
  const isOpen = open || hasActiveChild;

  return (
    <VStack data-testid={`nav-section-${id}`} className="pt-3">
      {/* eslint-disable-next-line design-tokens/no-raw-html-elements -- nav section toggle with uppercase tracking-wider text styling */}
      <button
        type="button"
        data-testid={`nav-section-toggle-${id}`}
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-left text-xs font-semibold tracking-wider uppercase text-[color:var(--on-surface-variant)] rounded focus:outline-none focus:ring-1 focus:ring-[color:var(--primary)] hover:text-[color:var(--on-surface)]"
      >
        <Text
          className={[
            "flex-1 text-left",
            hasActiveChild ? "text-[color:var(--on-surface)]" : "",
          ].join(" ")}
        >
          {title}
        </Text>
        <Text
          className={`transition-transform ${
            isOpen ? "rotate-0" : "-rotate-90"
          }`}
        >
          <Icon.ChevronDown size="sm" />
        </Text>
      </button>

      {isOpen ? (
        <VStack gap="xs">
          {items.map((item) => (
            <NavItem
              key={item.href}
              {...item}
              active={isActiveHref(item.href)}
              onClick={onItemClick}
            />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (s) => s.pathname });
  const { t: tAdminNav } = useTranslation("adminNav");
  const { t: tAddresses } = useTranslation("addresses");
  const { t: tBrokerNav } = useTranslation("brokerNav");
  const { t: tCommon } = useTranslation("common");
  const { t: tContacts } = useTranslation("contacts");
  const { t: tDashboard } = useTranslation("dashboard");
  const { t: tLoads } = useTranslation("loads");
  const { t: tNav } = useTranslation("nav");
  const { title } = usePageTitle();
  const { resolvedTheme } = useTheme();
  const [user, setUser] = useState<AuthUser | null>(() => getCurrentUser());
  const [overlay, setOverlay] = useState<"sidebar" | "qr" | null>(null);
  const closeSidebar = useCallback(() => setOverlay(null), []);

  // Cross-tab identity guard: reload if another tab signs in as a
  // different user or signs out, so this tab never transacts under a
  // silently swapped identity.
  useAuthStorageSync();

  // Listen for session expiration from the API client
  useEffect(() => {
    const handleSignout = () => setUser(null);
    window.addEventListener("auth:signout", handleSignout);
    return () => window.removeEventListener("auth:signout", handleSignout);
  }, []);

  const {
    entity: userEntity,
    loading: userEntityLoading,
    hasActiveApplication,
    error: userEntityError,
  } = useUserEntity();
  const [sectionOpen, setSectionOpen] = useState<{
    broker: Record<string, boolean>;
    admin: Record<string, boolean>;
  }>(() => ({
    broker: getBrokerSidebarSections(user?.id ?? null, BROKER_SECTION_DEFAULTS),
    admin: getBrokerSidebarSections(user?.id ?? null, ADMIN_SECTION_DEFAULTS),
  }));
  const isBroker = userEntity?.type === "Broker";
  const isDriver = user?.account_type === "Driver";
  const isCarrier = userEntity?.type === "TransportCompany" && !isDriver;
  const isAdmin = user?.is_admin === true;
  const isAdminRoute = (pathname ?? "").startsWith("/admin");

  // Routes that bypass the sidebar for unapproved users
  const isApplyRoute = (pathname ?? "").startsWith("/apply");
  const isApplicationStatusRoute = (pathname ?? "").startsWith(
    "/application-status",
  );
  const isUnapprovedUserRoute = isApplyRoute || isApplicationStatusRoute;

  useEffect(() => {
    if (!user) {
      const redirect = pathname && pathname !== "/" ? pathname : "";
      const loginUrl = redirect
        ? `/login?redirect=${encodeURIComponent(redirect)}`
        : "/login";
      void navigate({ to: loginUrl as never, replace: true });
    }
  }, [user, navigate, pathname]);

  useEffect(() => {
    if (user && isAdminRoute && !isAdmin) {
      void navigate({ to: "/dashboard", replace: true });
    }
  }, [isAdmin, isAdminRoute, navigate, user]);

  useEffect(() => {
    const p = pathname ?? "";

    // Bare admins must not create/edit business data from non-admin screens —
    // records would misfile under the System entity (yuklar-dkbnd B2).
    const isAdminBlockedLoadRoute =
      p === "/loads/new" || /^\/loads\/[^/]+\/edit$/.test(p);

    if (user && isAdmin && isAdminBlockedLoadRoute) {
      void navigate({ to: "/admin/dashboard", replace: true });
      return;
    }

    const isAllowedNonAdminRouteForAdmin = ADMIN_ALLOWED_ROUTES.some((prefix) =>
      p.startsWith(prefix),
    );

    if (user && isAdmin && !isAdminRoute && !isAllowedNonAdminRouteForAdmin) {
      void navigate({ to: "/admin/dashboard", replace: true });
    }
  }, [isAdmin, isAdminRoute, pathname, navigate, user]);

  // Redirect roles away from routes they cannot access
  useEffect(() => {
    if (!user || !userEntity) return;
    const p = pathname ?? "";
    const isShipper = userEntity.type === "Shipper";

    // /load-board requires Carrier or Broker (Shipper and Driver cannot access)
    if ((isShipper || isDriver) && p.startsWith("/load-board")) {
      void navigate({ to: "/dashboard", replace: true });
    }
    // /fleet requires Carrier (TransportCompany, non-driver)
    if (!isCarrier && p.startsWith("/fleet")) {
      void navigate({ to: "/dashboard", replace: true });
    }
    // /carriers and /shippers require Broker
    if (!isBroker && (p.startsWith("/carriers") || p.startsWith("/shippers"))) {
      void navigate({ to: "/dashboard", replace: true });
    }
  }, [user, userEntity, isCarrier, isBroker, isDriver, pathname, navigate]);

  // Redirect approved users away from /apply and /application-status
  useEffect(() => {
    if (userEntity && isUnapprovedUserRoute) {
      void navigate({ to: "/dashboard", replace: true });
    }
  }, [userEntity, isUnapprovedUserRoute, navigate]);

  // Redirect unapproved users to appropriate page
  // Do NOT redirect when there's a server/network error — the user may have an entity,
  // we just can't reach the backend to confirm it.
  useEffect(() => {
    if (
      !userEntityLoading &&
      !userEntity &&
      !userEntityError &&
      !isAdmin &&
      !isUnapprovedUserRoute
    ) {
      if (hasActiveApplication) {
        void navigate({ to: "/application-status", replace: true });
      } else {
        void navigate({ to: "/apply", replace: true });
      }
    }
  }, [
    userEntityLoading,
    userEntity,
    userEntityError,
    isAdmin,
    isUnapprovedUserRoute,
    hasActiveApplication,
    navigate,
  ]);

  const toggleBrokerSection = (sectionId: string) => {
    setSectionOpen((prev) => {
      const next = {
        ...prev,
        broker: {
          ...prev.broker,
          [sectionId]: !(prev.broker[sectionId] ?? true),
        },
      };
      setBrokerSidebarSections(user?.id ?? null, next.broker);
      return next;
    });
  };

  const toggleAdminSection = (sectionId: string) => {
    setSectionOpen((prev) => {
      const next = {
        ...prev,
        admin: {
          ...prev.admin,
          [sectionId]: !(prev.admin[sectionId] ?? true),
        },
      };
      setBrokerSidebarSections(user?.id ?? null, next.admin);
      return next;
    });
  };

  const handleLogout = async () => {
    await signOut();
    void navigate({ to: "/login", replace: true });
  };

  if (!user) {
    return (
      <VStack align="center" justify="center" className="min-h-screen">
        <Icon.Spinner size="xl" color="primary" />
      </VStack>
    );
  }

  if (userEntityLoading) {
    return (
      <VStack align="center" justify="center" className="min-h-screen">
        <Icon.Spinner size="xl" color="primary" />
      </VStack>
    );
  }

  // Gate: Backend unreachable — don't redirect, show error
  if (userEntityError && !userEntity) {
    return (
      <VStack align="center" justify="center" className="min-h-screen">
        <VStack align="center" gap="lg" className="max-w-sm text-center">
          <Icon.Alert size="xl" color="danger" />
          <Heading size="md">{tCommon("errorLoadingData")}</Heading>
          <Text color="muted">{tCommon("serverUnreachable")}</Text>
          <Button onClick={() => window.location.reload()}>
            {tCommon("retry")}
          </Button>
        </VStack>
      </VStack>
    );
  }

  // Gate: Users without an approved entity (and not admin) cannot access dashboard routes
  // They can only access /apply and /application-status
  if (!userEntity && !isAdmin) {
    // If user is already on an allowed route, render children directly (no sidebar)
    if (isUnapprovedUserRoute) {
      return <>{children}</>;
    }

    // Show loading while useEffect handles redirect
    return (
      <VStack align="center" justify="center" className="min-h-screen">
        <Icon.Spinner size="xl" color="primary" />
      </VStack>
    );
  }

  // Gate: Users WITH an approved entity should not access /apply or /application-status
  // useEffect handles the redirect, just show loading here
  if (userEntity && isUnapprovedUserRoute) {
    return (
      <VStack align="center" justify="center" className="min-h-screen">
        <Icon.Spinner size="xl" color="primary" />
      </VStack>
    );
  }

  // Gate: Role-restricted routes — show spinner while useEffect redirects
  if (userEntity) {
    const p = pathname ?? "";
    const isShipper = userEntity.type === "Shipper";
    if (
      ((isShipper || isDriver) && p.startsWith("/load-board")) ||
      (!isCarrier && p.startsWith("/fleet")) ||
      (!isBroker && (p.startsWith("/carriers") || p.startsWith("/shippers")))
    ) {
      return (
        <VStack align="center" justify="center" className="min-h-screen">
          <Icon.Spinner size="xl" color="primary" />
        </VStack>
      );
    }
  }

  const roleBadgeLabels: RoleBadgeLabels = {
    admin: tNav("roleBadge.admin"),
    carrier: tNav("roleBadge.carrier"),
    broker: tNav("roleBadge.broker"),
    driver: tNav("roleBadge.driver"),
    shipper: tNav("roleBadge.shipper"),
    owner: tNav("roleBadge.owner"),
    manager: tNav("roleBadge.manager"),
    operator: tNav("roleBadge.operator"),
    forwarder: tNav("roleBadge.forwarder"),
    accountant: tNav("roleBadge.accountant"),
  };

  const adminNavLabels: AdminNavLabels = {
    dashboard: tAdminNav("dashboard"),
    adminSection: tAdminNav("adminSection"),
    applications: tAdminNav("applications"),
    loadApprovals: tAdminNav("loadApprovals"),
    trips: tAdminNav("trips"),
    entities: tAdminNav("entities"),
    globalSettings: tAdminNav("globalSettings"),
    supportTickets: tAdminNav("supportTickets"),
    system: tAdminNav("system"),
    catalogsSection: tAdminNav("catalogsSection"),
    loadTypes: tAdminNav("loadTypes"),
    loadDocumentKinds: tAdminNav("loadDocumentKinds"),
    tripEventPolicies: tAdminNav("tripEventPolicies"),
    dangerousCategories: tAdminNav("dangerousCategories"),
    truckCatalog: tAdminNav("truckCatalog"),
    truckTypes: tAdminNav("truckTypes"),
    trailerTypes: tAdminNav("trailerTypes"),
    trailerLoadTypes: tAdminNav("trailerLoadTypes"),
    currencies: tAdminNav("currencies"),
    countries: tAdminNav("countries"),
    agreementTerms: tAdminNav("agreementTerms"),
    profile: tAdminNav("profile"),
  };

  const shipperNavLabels: ShipperNavLabels = {
    dashboard: tNav("dashboard"),
    myLoads: tLoads("myLoads"),
    negotiations: tNav("negotiations"),
    trips: tNav("trips"),
    addresses: tAddresses("title"),
    contacts: tContacts("title"),
    tickets: tNav("tickets"),
    account: tNav("account"),
  };

  const carrierNavLabels: CarrierNavLabels = {
    dashboard: tNav("dashboard"),
    loadBoard: tNav("loadBoard"),
    negotiations: tNav("negotiations"),
    trips: tNav("trips"),
    fleet: tNav("fleet"),
    drivers: tNav("drivers"),
    tickets: tNav("tickets"),
    account: tNav("account"),
  };

  const driverNavLabels: DriverNavLabels = {
    dashboard: tNav("dashboard"),
    myTrips: tNav("myTrips"),
    account: tNav("account"),
  };

  const brokerNavLabels: BrokerNavLabels = {
    overviewDashboard: tBrokerNav("overviewDashboard"),
    shipperOperations: tBrokerNav("shipperOperations"),
    postManageLoads: tBrokerNav("postManageLoads"),
    receivedBids: tBrokerNav("receivedBids"),
    loadTracking: tBrokerNav("loadTracking"),
    clientList: tBrokerNav("clientList"),
    carrierOperations: tBrokerNav("carrierOperations"),
    findLoads: tBrokerNav("findLoads"),
    myOffersSent: tBrokerNav("myOffersSent"),
    fleetDispatch: tBrokerNav("fleetDispatch"),
    providerList: tBrokerNav("providerList"),
    supportAndAccount: tBrokerNav("supportAndAccount"),
    tickets: tNav("tickets"),
    account: tNav("account"),
  };

  const roleBadgeLabel = (() => {
    if (isAdmin) return roleBadgeLabels.admin;
    switch (userEntity?.type) {
      case "TransportCompany":
        return roleBadgeLabels.carrier;
      case "Broker":
        return roleBadgeLabels.broker;
      case "Driver":
        return roleBadgeLabels.driver;
      case "Shipper":
      default:
        return roleBadgeLabels.shipper;
    }
  })();

  const brokerNavConfig = isBroker ? getBrokerNavConfig(brokerNavLabels) : null;
  const adminNavConfig = isAdmin ? getAdminNavConfig(adminNavLabels) : null;

  const navItems = isBroker
    ? []
    : isCarrier
      ? getCarrierNavItems(carrierNavLabels)
      : isDriver
        ? getDriverNavItems(driverNavLabels)
        : getShipperNavItems(shipperNavLabels);

  const isActive = (href: string) => {
    if (href === "/dashboard") {
      return pathname === "/dashboard";
    }
    return pathname?.startsWith(href) ?? false;
  };

  const roleUi = getRoleUiTokens({
    entityType: userEntity?.type ?? null,
    pathname: pathname ?? null,
    isAdmin: user?.is_admin,
    isDriver,
  });

  const themeTokens = resolvedTheme === "dark" ? roleUi.dark : roleUi.light;

  return (
    <Surface
      bg="background"
      data-testid="app-container"
      className="flex flex-col min-h-screen"
      style={
        {
          "--primary": themeTokens.primary,
          "--primary-hover": themeTokens.primaryHover,
          "--on-primary": themeTokens.onPrimary,
          "--primary-container": themeTokens.primaryContainer,
          "--role-nav-primary": roleUi.navPrimary,
        } as React.CSSProperties
      }
    >
      {/* Mobile sidebar backdrop */}
      {overlay === "sidebar" && (
        <Surface
          bg="scrim"
          data-testid="sidebar-backdrop"
          className="fixed inset-0 z-backdrop lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-overlay h-full w-64 bg-[color:var(--surface-container)] border-r border-[color:var(--outline-variant)] transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col ${
          overlay === "sidebar" ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo + Role badge */}
        <Surface
          border="bottom"
          data-testid="sidebar-logo"
          className="relative after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[color:var(--primary)] after:opacity-50 after:pointer-events-none"
        >
          <HStack justify="between" gap="none" className="px-4 min-h-16">
            <LogoImage />
            <IconButton
              type="button"
              data-testid="sidebar-close-btn"
              onClick={closeSidebar}
              className="lg:hidden text-[color:var(--on-surface-variant)] hover:text-[color:var(--on-surface)]"
            >
              <Icon.X size="lg" />
            </IconButton>
          </HStack>
          <VStack gap="xs" className="px-7 pb-2">
            {(userEntity?.name || user.entity?.name) && (
              <Text size="xs" color="muted" truncate>
                {userEntity?.name || user.entity?.name}
              </Text>
            )}
            <Text size="label" weight="medium" color="primary">
              {roleBadgeLabel}
            </Text>
          </VStack>
        </Surface>

        {/* Navigation */}
        <nav
          data-testid="sidebar-nav"
          className="flex-1 p-4 space-y-1 overflow-y-auto"
        >
          {isAdmin && adminNavConfig ? (
            <>
              <NavItem
                {...adminNavConfig.overview}
                active={isActive(adminNavConfig.overview.href)}
                onClick={closeSidebar}
              />
              {adminNavConfig.sections.map((section) => (
                <NavSection
                  key={section.id}
                  id={section.id}
                  title={section.title}
                  items={section.items}
                  open={sectionOpen.admin[section.id] ?? true}
                  onToggle={() => toggleAdminSection(section.id)}
                  isActiveHref={isActive}
                  onItemClick={closeSidebar}
                />
              ))}
              <NavItem
                {...adminNavConfig.profile}
                active={isActive(adminNavConfig.profile.href)}
                onClick={closeSidebar}
              />
            </>
          ) : isBroker && brokerNavConfig ? (
            <>
              <NavItem
                {...brokerNavConfig.overview}
                active={isActive(brokerNavConfig.overview.href)}
                onClick={closeSidebar}
              />
              {brokerNavConfig.sections.map((section) => (
                <NavSection
                  key={section.id}
                  id={section.id}
                  title={section.title}
                  items={section.items}
                  open={sectionOpen.broker[section.id] ?? true}
                  onToggle={() => toggleBrokerSection(section.id)}
                  isActiveHref={isActive}
                  onItemClick={closeSidebar}
                />
              ))}
            </>
          ) : isCarrier ? (
            <>
              {navItems.map((item) => (
                <NavItem
                  key={item.href}
                  {...item}
                  active={isActive(item.href)}
                  onClick={closeSidebar}
                />
              ))}
            </>
          ) : (
            navItems.map((item) => (
              <NavItem
                key={item.href}
                {...item}
                active={isActive(item.href)}
                onClick={closeSidebar}
              />
            ))
          )}
        </nav>

        {/* Scan QR Button */}
        <Surface border="top" className="mt-auto p-4">
          <Button
            type="button"
            color="neutral"
            variant="outline"
            size="md"
            fullWidth
            onClick={() => setOverlay("qr")}
            data-testid="sidebar-scan-qr"
          >
            <Icon.ScanQr size="sm" className="mr-2" />
            {tNav("scanQr")}
          </Button>
        </Surface>

        {/* Support Card */}
        {!isBroker && !isAdmin ? (
          <Surface
            border="top"
            data-testid="sidebar-support"
            className="px-4 py-2"
          >
            <HStack>
              <Icon.Support color="muted" />
              <Text color="muted">{tDashboard("support")} · 24/7</Text>
            </HStack>
          </Surface>
        ) : null}
      </aside>

      {/* Main content area */}
      <Surface
        bg="background"
        data-testid="main-content-area"
        className="flex flex-col lg:ml-64 min-h-screen transition-colors"
      >
        {/* Top bar */}
        <header
          data-testid="header"
          className="h-16 bg-[color:var(--surface-container)] border-b border-[color:var(--outline-variant)] sticky top-0 z-header relative after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[color:var(--primary)] after:opacity-50 after:pointer-events-none"
        >
          <HStack justify="between" className="h-full px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <IconButton
              type="button"
              size="md"
              data-testid="header-menu-btn"
              onClick={() => setOverlay("sidebar")}
              className="lg:hidden text-[color:var(--on-surface-variant)] hover:text-[color:var(--on-surface)]"
            >
              <Icon.Menu size="lg" />
            </IconButton>

            {/* Page Title */}
            {title && (
              <Heading as="h1" size="xl" data-testid="header-page-title">
                {title}
              </Heading>
            )}

            {/* Right side actions */}
            <HeaderActions
              user={{
                name: user.name,
                roleBadge: (
                  {
                    SuperAdmin: roleBadgeLabels.admin,
                    Admin: roleBadgeLabels.admin,
                    Owner: roleBadgeLabels.owner,
                    Manager: roleBadgeLabels.manager,
                    Operator: roleBadgeLabels.operator,
                    Driver: roleBadgeLabels.driver,
                    Forwarder: roleBadgeLabels.forwarder,
                    Accountant: roleBadgeLabels.accountant,
                  } as Record<string, string>
                )[user.role],
              }}
              onLogout={handleLogout}
            />
          </HStack>
        </header>

        {/* Page content */}
        <main data-testid="main-content" className="p-6">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </Surface>

      <QrScannerModal
        isOpen={overlay === "qr"}
        onClose={() => setOverlay(null)}
        onScanSuccess={async (code) => {
          setOverlay(null);
          const trimmed = code.trim();
          // If it's a URL on our domain, navigate directly to its path
          try {
            const url = new URL(trimmed);
            const appUrl = APP_URL;
            if (appUrl && url.origin === new URL(appUrl).origin) {
              void navigate({
                to: (url.pathname + url.search + url.hash) as never,
              });
              return;
            }
          } catch {
            // Not a URL — treat as QR token code
          }
          const extracted = extractQrCode(trimmed);
          void navigate({ to: "/qr/$code", params: { code: extracted } });
        }}
        requirePin={false}
        title={tNav("scanQr")}
        data-testid="sidebar-qr-scanner"
      />
    </Surface>
  );
}

function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <PageTitleProvider>
      <UserEntityProvider>
        <WorkspaceModeProvider>
          <LayoutContent>{children}</LayoutContent>
        </WorkspaceModeProvider>
      </UserEntityProvider>
    </PageTitleProvider>
  );
}

export const Route = createFileRoute("/_app")({
  component: function LayoutRoute() {
    return (
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    );
  },
});
